import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Card, Button } from '../../components/v2/V2Components';
import SubscriptionService from '../../services/SubscriptionService';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 5; // ~10s — Stripe's webhook provisions the tier asynchronously.

// Terminal answers from GET /subscriptions/checkout-status → page state. Anything
// else ('pending', 'unpaid', unknown, or a failed call) means "ask again".
const SESSION_OUTCOMES = { paid: 'active', ended: 'ended', duplicate: 'duplicate' };

/**
 * Landing page after a successful Stripe Checkout.
 *
 * Stripe substitutes `?session_id=cs_…` into the success URL. When it is present,
 * confirmation comes ONLY from `GET /subscriptions/checkout-status` for that
 * session: it verifies the session is ours and, if Stripe reports its
 * subscription paid and live, provisions the tier through the same path the
 * webhook uses. The account's `subscription_tier` is deliberately not consulted
 * — another subscription may be paying for the account, and that must not read
 * as this checkout succeeding. The endpoint is polled until it answers `paid`
 * (success), `ended` (the session's subscription has since been canceled) or
 * `duplicate` (the account already holds a different live subscription, so this
 * one was not applied), or the poll budget runs out ("pending" with Check again).
 *
 * Without a session id (older links), we fall back to polling the limits
 * endpoint for the webhook to flip the tier, as before.
 */
export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('confirming'); // 'confirming' | 'active' | 'pending' | 'ended' | 'duplicate'
  const [rechecking, setRechecking] = useState(false);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);

  // One probe. Resolves a page state ('active' | 'ended' | 'duplicate') when the
  // answer is terminal, or null when we should ask again (pending, unpaid, a
  // transient failure, or — without a session id — the tier not yet flipped).
  const probe = useCallback(async () => {
    if (sessionId) {
      try {
        const data = await SubscriptionService.getCheckoutStatus(sessionId);
        return SESSION_OUTCOMES[data?.status] ?? null;
      } catch {
        return null;
      }
    }
    try {
      const data = await SubscriptionService.getLimits();
      return data?.subscription_tier === 'paid' ? 'active' : null;
    } catch {
      return null; // transient — caller decides whether to keep polling
    }
  }, [sessionId]);

  useEffect(() => {
    cancelledRef.current = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      const outcome = await probe();
      if (cancelledRef.current) return;
      if (outcome) {
        setStatus(outcome);
        return;
      }
      if (attempts >= MAX_POLLS) {
        // Provisioning hasn't reflected in ~10s. Usually just lag, but it can also
        // mean the webhook is failing — leave a dev breadcrumb and show an
        // actionable "pending" state rather than asserting success.
        if (import.meta.env?.DEV) {
          console.warn(`SubscriptionSuccess: not confirmed after ${attempts} polls`);
        }
        setStatus('pending');
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [probe]);

  const handleRecheck = async () => {
    setRechecking(true);
    const outcome = await probe();
    setRechecking(false);
    if (outcome) setStatus(outcome);
  };

  const isTerminalProblem = status === 'pending' || status === 'ended' || status === 'duplicate';

  return (
    <div className="max-w-md mx-auto p-4">
      <Card className="p-8 text-center">
        {status === 'active' ? (
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-[var(--primary)]" />
        ) : isTerminalProblem ? (
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[var(--text-secondary)]" />
        ) : (
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-[var(--primary)] animate-spin" />
        )}

        <h1 className="text-xl font-semibold text-[var(--text-main)] mb-2">
          {status === 'active'
            ? 'Welcome to Premium!'
            : status === 'pending'
              ? 'Almost there…'
              : status === 'ended'
                ? 'This subscription is no longer active'
                : status === 'duplicate'
                  ? 'You already have a subscription'
                  : 'Finalizing your upgrade…'}
        </h1>

        <p className="text-sm text-[var(--text-secondary)] mb-6">
          {status === 'active' && 'Your subscription is active. Your higher limits are ready to use.'}
          {status === 'confirming' &&
            'Thanks for upgrading! We’re confirming your subscription — this only takes a moment.'}
          {status === 'pending' &&
            'Your payment was received, but your account hasn’t updated yet. This usually resolves within a minute — try “Check again,” or contact support if it persists.'}
          {status === 'ended' &&
            'The subscription from this checkout has since been canceled or has lapsed, so nothing on your account was changed. Your current plan is on the Plan & Usage page, where you can upgrade again if you meant to.'}
          {status === 'duplicate' &&
            'You already have an active subscription, so this purchase was not applied. Open Manage billing to review it.'}
        </p>

        {status === 'pending' && (
          <Button variant="secondary" fullWidth loading={rechecking} onClick={handleRecheck} className="mb-2">
            Check again
          </Button>
        )}
        <Button variant="primary" fullWidth onClick={() => navigate('/subscription')}>
          View plan &amp; usage
        </Button>
      </Card>
    </div>
  );
}
